import { View } from 'react-native';

import {
  ActionSheet,
  AlertDialog,
  Button,
  IconDelivery,
  IconLocationFill,
  MqSwitch,
  Text,
  createMqStyles,
  tokens,
} from '@fhs/ui';
import noop from 'utils/noop';

type DeliveryClosedActionSheetProps = {
  isOpen: boolean;
  address?: string;
  onChangeDeliveryAddress: () => void;
  onPickUpElsewhere: () => void;
};

const title = 'Your Selected Delivery Restaurant is Closed';
const bodyText =
  "We're sorry, this restaurant is currently closed. Try a different delivery address or choose another location for pickup.";

export function DeliveryClosedActionSheet({
  isOpen,
  address,
  onChangeDeliveryAddress,
  onPickUpElsewhere,
}: DeliveryClosedActionSheetProps) {
  const styles = useStyles();

  const heading = (
    <View style={styles.heading}>
      <View style={styles.iconContainer}>
        <IconDelivery size={40} />
      </View>
      <Text.Heading type="three" selectable={false} style={styles.text}>
        {title}
      </Text.Heading>
    </View>
  );

  const body = (
    <View style={styles.body}>
      <Text.Paragraph size="md" selectable={false} style={styles.text}>
        {bodyText}
      </Text.Paragraph>
      {!!address && (
        <View style={styles.addressBox}>
          <View style={styles.addressIconContainer}>
            <IconLocationFill size={20} color={tokens.colors.$houseDark} />
          </View>
          <View style={styles.addressTextContainer}>
            <Text.Ui size="sm" style={styles.addressLabel}>
              Your Address:
            </Text.Ui>
            <Text.Ui size="md" weight="semibold" style={styles.addressText}>
              {address}
            </Text.Ui>
          </View>
        </View>
      )}
    </View>
  );

  const actions = (
    <>
      <Button size="xl" type="solid" onPress={onChangeDeliveryAddress}>
        <Button.Text>Change Delivery Address</Button.Text>
      </Button>
      <Button size="lg" type="outline" onPress={onPickUpElsewhere}>
        <Button.Text>Pick Up Elsewhere</Button.Text>
      </Button>
    </>
  );

  return (
    <MqSwitch
      $base={() => (
        <ActionSheet
          isVisible={isOpen}
          onClose={noop}
          onRequestClose={noop}
          noPanGestureDetection
          sheetAccessibilityLabel={title}
          drawerProps={{ style: styles.drawer }}
        >
          <View style={styles.sheetContainer}>
            {heading}
            {body}
            <View style={styles.sheetActions}>{actions}</View>
          </View>
        </ActionSheet>
      )}
      $gteDesktop={() => (
        <AlertDialog
          visible={isOpen}
          accessibilityLabel={title}
          headingContent={heading}
          bodyContent={body}
          hideCloseIcon
          backdropStyle={styles.backdrop}
          style={styles.dialog}
          onRequestClose={noop}
          onDismiss={noop}
          onClose={noop}
          actions={actions}
        />
      )}
    />
  );
}

const useStyles = createMqStyles({
  backdrop: {
    $base: {
      overflow: 'hidden',
    },
  },
  dialog: {
    $base: {
      maxWidth: 580,
    },
  },
  drawer: {
    $base: {
      width: '100%',
    },
  },
  sheetContainer: {
    $base: {
      width: '100%',
      padding: 16,
      gap: 12,
      alignItems: 'center',
    },
  },
  sheetActions: {
    $base: {
      width: '100%',
      gap: 8,
      marginTop: 4,
    },
  },
  heading: {
    $base: {
      width: '100%',
      alignItems: 'center',
      gap: 12,
    },
  },
  body: {
    $base: {
      width: '100%',
      alignItems: 'center',
      gap: 12,
    },
  },
  iconContainer: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 100,
      padding: 10,
    },
  },
  text: {
    $base: {
      textAlign: 'center',
      width: '100%',
    },
  },
  addressBox: {
    $base: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: 16,
      borderWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
      borderRadius: 12,
    },
  },
  addressIconContainer: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 100,
      padding: 8,
      flexShrink: 0,
    },
  },
  addressTextContainer: {
    $base: {
      flex: 1,
      minWidth: 0,
      flexShrink: 1,
      gap: 2,
    },
  },
  addressLabel: {
    $base: {
      color: tokens.colors.$blackOpacity55,
    },
  },
  addressText: {
    $base: {
      flexShrink: 1,
    },
  },
});
